import { useWalletBalance, useWalletError } from "@sundae/wallet-react";
import { useMemo } from "react";

import { SundaeUtils } from "@sundaeswap/core/utilities";
import { IAssetMetaData } from "../types/Asset.types";
import { getAssetId } from "../utils/assets.utils";
import useAppSearch from "./router/useAppSearch";
import { useAdaBalance } from "./useAdaBalance";
import useIsLPAsset from "./useIsLPAsset";

export const useAssetsWithBalance = () => {
  const { given, taken } = useAppSearch();
  const { error } = useWalletError();
  const { balance, query: balanceQuery, isFetched } = useWalletBalance();
  const { availableAdaBalance } = useAdaBalance();
  const { isLPAsset } = useIsLPAsset();

  const assetsWithBalance = useMemo(() => {
    if (!balance) return [];

    const assets = balance.getFungibleAssets<IAssetMetaData>().map(([_key, asset]) => asset);

    return assets;
  }, [balance, availableAdaBalance]);

  const searchAssetsBalance = useMemo(() => {
    if (assetsWithBalance?.length === 0) {
      return {};
    }

    return {
      givenAssetBalance: assetsWithBalance.find(
        ({ metadata }) => given && SundaeUtils.isAssetIdsEqual(given, getAssetId(metadata))
      ),
      takenAssetBalance: assetsWithBalance.find(
        ({ metadata }) => taken && SundaeUtils.isAssetIdsEqual(taken, getAssetId(metadata))
      ),
    };
  }, [given, taken, assetsWithBalance]);

  const assetsWithoutLP = useMemo(
    () => assetsWithBalance.filter(({ metadata }) => !isLPAsset(metadata.policyId)),
    [assetsWithBalance, isLPAsset]
  );

  const lpAssets = useMemo(() => {
    return assetsWithBalance.filter(({ metadata }) => isLPAsset(metadata.policyId));
  }, [assetsWithBalance, isLPAsset]);

  /**
   * An array of LP assetIds that the user has in their wallet.
   * Useful for fetching pool data for LPs only.
   */
  const lpAssetIds = useMemo(
    () =>
      assetsWithBalance
        .filter((asset) => isLPAsset(asset.metadata.policyId))
        .map((asset) => asset.metadata.assetId),
    [assetsWithBalance, isLPAsset]
  );

  return {
    searchAssetsBalance,
    assetsWithBalance,
    assetsWithoutLP,
    balanceLoaded: Boolean(balance),
    balanceReloading: balanceQuery.isRefetching,
    error,
    lpAssets,
    lpAssetIds,
    isFetched,
  };
};
